import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';

/**
 * Celda de grilla con un valor de sólo lectura que, si el usuario tiene permiso, se puede editar
 * eligiendo otra opción en un desplegable con búsqueda contra el backend.
 *
 * Sale de VEC-3572 (editar el Centro de Costos desde la grilla de Vehículos).
 *
 * *** LO QUE COMPARTE CON LAS OTRAS CELDAS EDITABLES ES EL ENVOLTORIO, NO EL INPUT. ***
 * `MovilesGridRow.jsx` tiene otras tres celdas que se editan con un chevron y un panel flotante
 * —Vehículo Reserva, Estado y Ruta— y las cuatro repiten las mismas ~20 líneas: el texto, el
 * chevron, el panel y el efecto que lo cierra al clickear afuera. El panel se repite además en
 * los dos grids de Presupuesto Detallado y en el de Eric.
 *
 * Pero el control de adentro es distinto en cada una, verificado probándolas en la UI:
 *
 *   - Centro de Costos (ésta): opción única con búsqueda contra el backend (`Select.Async`).
 *   - Vehículo Reserva: un `<input>` de texto con una lista filtrada a mano, sin react-select.
 *   - Ruta: selección MÚLTIPLE, con `<Chip>` por ruta elegida.
 *   - Estado: lista acotada (no busca nada) y cada opción lleva su color.
 *
 * Por eso este componente resuelve un caso concreto —el select async de opción única— y no
 * pretende ser "la celda editable" de las grillas. Si alguna de las otras tres se quiere adoptar,
 * el corte natural es extraer primero el envoltorio y dejar el control como contenido; hacerlo
 * ahora, con un solo consumidor, sería diseñar la API a ciegas.
 *
 *     <GridCellSelect
 *         label={cecos && cecos.nombre}
 *         editable={puedeEditar}
 *         value={cecos ? { value: cecos.id, label: cecos.nombre } : null}
 *         loadOptions={buscarCentrosCosto}
 *         onChange={handleSeleccion}
 *         titulo="Nuevo Centro de Costo"
 *     />
 *
 * *** NO MIGRA A NADIE. *** Nace usada sólo por la celda de Centro de Costos, y es la regla de
 * `docs/design-system/grid-piezas-compartidas.md`: construir una pieza no obliga a adoptarla en
 * las grillas que ya funcionan.
 *
 * Es presentacional: no sabe qué se está editando ni cómo se guarda. Decide QUÉ mostrar y CUÁNDO
 * avisar; confirmar, persistir y refrescar la fila es del que la usa.
 */

/** Estilos del panel flotante. Son los que ya usaba el desplegable de Ruta, para que no cambie. */
const ESTILO_PANEL = {
	border: '1px solid #ccc',
	padding: '10px',
	backgroundColor: '#fff',
	position: 'absolute',
	zIndex: 10,
	borderRadius: '4px',
	minWidth: '260px',
};

const ESTILO_CHEVRON = {
	marginRight: '5px',
	marginLeft: '5px',
	fontSize: '11px',
	marginBlock: '6px',
};

/**
 * Si la opción elegida representa un cambio real.
 *
 * Es la única lógica del componente, y está afuera para poder testearla: el proyecto no tiene
 * renderer de componentes en los tests (mismo motivo por el que `movilesGridRowData.js` vive
 * aparte). Elegir en el desplegable la MISMA opción que ya estaba no debe disparar nada: si no,
 * la pantalla abre un modal de confirmación para un cambio que no existe, y en el caso de Centro
 * de Costos eso además ensucia el histórico con un movimiento que no cambió nada.
 *
 * `null`/`undefined` como opción tampoco es un cambio: react-select llama a onChange con null
 * cuando se limpia el campo, y estas celdas no soportan "sin valor".
 */
export const esSeleccionNueva = (opcion, valorActual) => {
	if (!opcion) return false;
	if (!valorActual) return true;

	return opcion.value !== valorActual.value;
};

const GridCellSelect = (props) => {
	const { label, editable, value, loadOptions, onChange, titulo, id, placeholder } = props;

	const [abierto, setAbierto] = useState(false);
	const contenedorRef = useRef(null);

	// Cierra al clickear afuera. El efecto sólo se engancha con el panel abierto: si escuchara
	// siempre, cada fila de la grilla dejaría un listener de mousedown en el document.
	useEffect(() => {
		if (!abierto) return undefined;

		const alClickearAfuera = (evento) => {
			if (contenedorRef.current && !contenedorRef.current.contains(evento.target)) {
				setAbierto(false);
			}
		};

		document.addEventListener('mousedown', alClickearAfuera);
		return () => document.removeEventListener('mousedown', alClickearAfuera);
	}, [abierto]);

	const alElegir = (opcion) => {
		setAbierto(false);
		if (esSeleccionNueva(opcion, value)) onChange(opcion);
	};

	return (
		<div className="d-flex align-items-center">
			<div>{label}</div>
			{editable ? (
				<div>
					<i
						className="fa fa-chevron-down fa-2xs cursor-pointer"
						style={ESTILO_CHEVRON}
						onClick={() => setAbierto((prev) => !prev)}
					></i>
					{abierto && (
						<div style={{ position: 'relative' }} ref={contenedorRef}>
							<div style={{ position: 'static' }}>
								<div style={ESTILO_PANEL}>
									{titulo ? <div style={{ marginBottom: '5px' }}>{titulo}:</div> : null}
									<Select.Async
										id={id}
										name={id}
										valueKey="value"
										labelKey="label"
										value={value}
										placeholder={placeholder}
										loadOptions={loadOptions}
										onChange={alElegir}
										// Sin opción de vaciar: estas celdas editan un campo que ya tiene valor y no
										// soportan "sin asignar" (en Centro de Costos el backend rechaza un id vacío).
										// Es lo mismo que hace el ABM en ese campo.
										clearable={false}
									/>
								</div>
							</div>
						</div>
					)}
				</div>
			) : ''}
		</div>
	);
};

export default GridCellSelect;
